import { useIntervalFn } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { PerspectiveReport } from "~vue/appTypes";
import { type MaybeRefOrGetter, ref, toValue, watch } from "vue";

const POLL_INTERVAL_MS = 10_000;

export interface UsePerspectiveReportImportOptions {
    /** Whether the PERSPECTIVES_REPORT (Team Tools) flag is enabled. */
    reportEnabled: MaybeRefOrGetter<boolean>;
    /** Whether the in-app PERSPECTIVE_ASSESSMENT flag is enabled. */
    assessmentEnabled: MaybeRefOrGetter<boolean>;
    /** Any report already present on page load. */
    initialReport?: PerspectiveReport | null;
    /** Called once when a report is first found (e.g. to reload stale page props). */
    onReportFound?: (report: PerspectiveReport) => void;
    /**
     * When true, and a report already exists on load, fire a single refresh check
     * anyway to pick up a Team Tools retake. The backend's timestamp guard makes this
     * a cheap no-op when OpenSearch has nothing newer, so it's safe as a one-shot
     * per page load. Defaults to false so pages that only care about the initial
     * "no report yet" import (e.g. the home page) aren't affected.
     */
    refreshExistingReport?: boolean;
}

/**
 * Owns the legacy Team Tools perspective import: polls `/import-perspective-report/`
 * until a report is available. Polling only runs when the report flag is on, the in-app
 * assessment flag is off (in-app takes precedence), and no report exists yet.
 *
 * When `refreshExistingReport` is set, a single refresh call also fires on load even
 * if a report already exists, to pick up a Team Tools retake.
 *
 * Lives in a composable so the polling isn't tied to the visibility/priority of any one
 * home-page card.
 */
export function usePerspectiveReportImport({
    reportEnabled,
    assessmentEnabled,
    initialReport = null,
    onReportFound,
    refreshExistingReport = false,
}: UsePerspectiveReportImportOptions) {
    const perspectiveReport = ref<PerspectiveReport | null>(initialReport);

    const { execute, isFetching, data } = useFetch("/import-perspective-report/", { immediate: false }).post().json();

    async function fetchLatestReport(): Promise<PerspectiveReport | undefined> {
        await execute();
        return data.value?.perspective_report;
    }

    async function refresh() {
        if (perspectiveReport.value) {
            return;
        }
        const report = await fetchLatestReport();
        if (report) {
            perspectiveReport.value = report;
        }
    }

    const shouldPoll = () => toValue(reportEnabled) && !toValue(assessmentEnabled) && !perspectiveReport.value;

    const interval = useIntervalFn(refresh, POLL_INTERVAL_MS, { immediate: shouldPoll() });

    // Stop polling once a report is present.
    watch(perspectiveReport, (report) => {
        if (report) {
            interval.pause();
            onReportFound?.(report);
        }
    });

    if (refreshExistingReport && perspectiveReport.value && toValue(reportEnabled) && !toValue(assessmentEnabled)) {
        void fetchLatestReport().then((report) => {
            if (report && perspectiveReport.value && new Date(report.updated_at).getTime() > new Date(perspectiveReport.value.updated_at).getTime()) {
                perspectiveReport.value = report;
            }
        });
    }

    return { perspectiveReport, refresh, isFetching };
}
